import React, { useState, memo } from 'react';
import { User, Info, Users, Briefcase, Building, BarChart3, Building2, MapPin, Calendar, DollarSign, Target, ChevronDown, ChevronUp, GraduationCap, Heart, MessageSquare, Lightbulb, Star, TrendingUp, AlertTriangle, ChevronRight, Zap, ShoppingCart, Megaphone, Brain, Activity } from 'lucide-react';
import { cn } from '../../lib/utils';
import SegmentAnalyticsModal from './SegmentAnalyticsModal';
import ReactMarkdown from 'react-markdown';
import { getZIndexClass } from '../../config/zIndex';

interface SegmentOverview {
  age_range?: string;
  avg_age?: string;
  income_range?: string;
  gender_split?: Record<string, number>;
  persona_count?: number;
  top_locations?: string[];
  top_behaviors?: string[];
  top_interests?: string[];
  industry_breakdown?: Record<string, number>;
  role_breakdown?: Record<string, number>;
}

interface MacroCluster {
  name: string;
  share_of_personas_pct: number;
  defining_hook: string;
  subsegments_included: string[];
  relevant_traits: string[];
}

interface QuantRollups {
  ocean_avg_all: {
    O: number;
    C: number;
    E: number;
    A: number;
    N: number;
  };
  categorical_distributions: {
    fandom_level: Record<string, number>;
    faith_guided_pct: number;
    time_pressure_pct: number;
  };
}

interface SegmentDemographics {
  segment_overviews?: SegmentOverview[];
  // New format fields (for segments 130-133)
  exec_summary?: string;
  macro_clusters?: MacroCluster[];
  quant_rollups?: QuantRollups;
  // Old format fields  
  segments?: any[];
  segment_name?: string;
  total_personas?: number;
  age_analysis?: any;
  income_analysis?: any;
  locations?: any[];
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number;
  created_at: string;
  updated_at: string;
  // Filter attributes for preview tags
  industryL1?: string[];
  industryL2?: string[];
  functions?: string[];
  roles?: string[];
  [key: string]: any;
}

interface SegmentCardProps {
  segment: Segment;
  isSelected: boolean;
  isLoading?: boolean;
  demographics?: SegmentDemographics;
  loadingDemographics?: boolean;
  previewTags: string[];
  audienceType: 'b2b' | 'b2c' | 'simple';
  personaCount?: number;
  loadingCount?: boolean;
  totalAudienceSize?: number;
  onToggle: (segmentId: number) => void;
  onInfoHover?: (segmentId: number, event: React.MouseEvent) => void;
  onInfoLeave?: () => void;
  onViewPersonas?: (segmentId: number) => void;
  className?: string;
  // Filter props
  filterConfig?: Record<string, any>;
  filterValues?: Record<string, any>;
  onFilterChange?: (segmentId: number, filterKey: string, values: string[]) => void;
  onUpdateCount?: (segmentId: number) => void;
  onEditStep?: () => void;
}

const SegmentCard: React.FC<SegmentCardProps> = ({
  segment,
  isSelected,
  demographics,
  personaCount,
  loadingCount = false,
  totalAudienceSize,
  onToggle,
  onInfoHover,
  onInfoLeave,
  onViewPersonas,
  className = '',
  // Filter props
  filterConfig = {},
  filterValues = {},
  onFilterChange,
  onUpdateCount,
  onEditStep
}) => {
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showMoreDetails, setShowMoreDetails] = useState<Record<number, boolean>>({});
  const [expandedHierarchicalFilters, setExpandedHierarchicalFilters] = useState<Record<string, boolean>>({});
  const [expandedClusters, setExpandedClusters] = useState<Record<number, boolean>>({});


  const handleCardClick = () => {
    onToggle(segment.id);
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onInfoHover) {
      onInfoHover(segment.id, e);
    }
  };

  const handleViewPersonasClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onViewPersonas) {
      onViewPersonas(segment.id);
    }
  };

  // Filter helper functions
  const isFilterSelected = (filterKey: string, value: string): boolean => {
    const selectedValues = filterValues[filterKey] || [];
    return selectedValues.includes(value);
  };

  const toggleFilter = (filterKey: string, value: string, isChecked: boolean) => {
    if (!onFilterChange) return;
    
    const currentValues = filterValues[filterKey] || [];
    let newValues: string[];
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter((v: string) => v !== value);
    }
    
    onFilterChange(segment.id, filterKey, newValues);
    
    // Remove immediate count update to prevent race conditions
    // The count will be updated by the debounced handler in the parent component
    
    // Notify parent of edit
    if (onEditStep) {
      onEditStep();
    }
  };

  // Helper function to check if a filter config has hierarchical options
  const isHierarchicalFilter = (config: any): boolean => {
    return config.type === 'hierarchical' && 
           config.options && 
           typeof config.options === 'object' && 
           !Array.isArray(config.options);
  };

  // Helper function to toggle hierarchical filter expansion
  const toggleHierarchicalExpansion = (filterKey: string, parentKey: string) => {
    const expansionKey = `${filterKey}-${parentKey}`;
    setExpandedHierarchicalFilters(prev => ({
      ...prev,
      [expansionKey]: !prev[expansionKey]
    }));
  };

  const clearAllFilters = () => {
    if (!onFilterChange) return;
    
    Object.keys(filterConfig).forEach(filterKey => {
      onFilterChange(segment.id, filterKey, []);
    });
    
    // Remove immediate count update to prevent race conditions
    // The count will be updated by the debounced handler in the parent component
    
    if (onEditStep) {
      onEditStep();
    }
  };

  const selectAllFilters = () => {
    if (!onFilterChange) return;
    
    Object.entries(filterConfig).forEach(([filterKey, config]: [string, any]) => {
      if (config.options) {
        if (isHierarchicalFilter(config)) {
          // For hierarchical filters, select all sub-options from all parents
          const allOptions: string[] = [];
          Object.values(config.options).forEach((subOptions: any) => {
            if (Array.isArray(subOptions)) {
              allOptions.push(...subOptions);
            }
          });
          onFilterChange(segment.id, filterKey, allOptions);
        } else if (Array.isArray(config.options)) {
          onFilterChange(segment.id, filterKey, [...config.options]);
        }
      }
    });
    
    // Remove immediate count update to prevent race conditions
    // The count will be updated by the debounced handler in the parent component
    
    if (onEditStep) {
      onEditStep();
    }
  };

  const toggleClusterExpansion = (index: number) => {
    setExpandedClusters(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const renderOceanScore = (scores: QuantRollups['ocean_avg_all']) => {
    const traits = [
      { key: 'O', label: 'Openness', color: 'bg-blue-500' },
      { key: 'C', label: 'Conscientiousness', color: 'bg-green-500' },
      { key: 'E', label: 'Extraversion', color: 'bg-yellow-500' },
      { key: 'A', label: 'Agreeableness', color: 'bg-purple-500' },
      { key: 'N', label: 'Neuroticism', color: 'bg-red-500' }
    ];

    return (
      <div className="space-y-2">
        {traits.map(trait => (
          <div key={trait.key} className="flex items-center gap-2">
            <span className="w-28 text-xs font-medium text-gray-700">{trait.label}</span>
            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
              <div
                className={cn(trait.color, 'h-1.5 rounded-full transition-all duration-500')}
                style={{ width: `${scores[trait.key as keyof typeof scores] * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-600 w-10">
              {(scores[trait.key as keyof typeof scores] * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderNewFormatDetails = () => {
    const data = demographics as SegmentDemographics;
    
    return (
      <div className="space-y-6">
        {/* Executive Summary */}
        {data.exec_summary && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Executive Summary</h3>
                <p className="text-sm text-gray-700 leading-relaxed">{data.exec_summary}</p>
              </div>
            </div>
          </div>
        )}

        {/* Macro Clusters */}
        {data.macro_clusters && data.macro_clusters.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Audience Clusters
            </h3>
            <div className="space-y-3">
              {data.macro_clusters.map((cluster, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className="p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleClusterExpansion(index)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-gray-900">{cluster.name}</h4>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                            {cluster.share_of_personas_pct}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 italic">"{cluster.defining_hook}"</p>
                      </div>
                      <button className="p-1">
                        {expandedClusters[index] ? 
                          <ChevronUp className="w-4 h-4 text-gray-500" /> : 
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        }
                      </button>
                    </div>
                  </div>
                  
                  {expandedClusters[index] && (
                    <div className="p-3 bg-white border-t border-gray-200">
                      <div className="space-y-3">
                        {cluster.subsegments_included?.length > 0 && (
                          <div>
                            <h5 className="font-medium text-xs text-gray-700 mb-1">Subsegments:</h5>
                            <div className="flex flex-wrap gap-1">
                              {cluster.subsegments_included.map((segment, segIndex) => (
                                <span key={segIndex} className="bg-gray-100 text-gray-700 text-xs px-1.5 py-0.5 rounded">
                                  {segment}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {cluster.relevant_traits?.length > 0 && (
                          <div>
                            <h5 className="font-medium text-xs text-gray-700 mb-1">Key Traits:</h5>
                            <ul className="space-y-0.5">
                              {cluster.relevant_traits.slice(0, 3).map((trait, traitIndex) => (
                                <li key={traitIndex} className="flex items-start gap-1 text-xs text-gray-600">
                                  <span className="text-blue-500 mt-0.5">•</span>
                                  <span>{trait}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quantitative Metrics */}
        {data.quant_rollups && (
          <div className="space-y-4">
            {/* OCEAN Scores */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4 text-purple-600" />
                <h4 className="text-sm font-semibold text-gray-900">Personality Profile</h4>
              </div>
              {renderOceanScore(data.quant_rollups.ocean_avg_all)}
            </div>
            
            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Zap className="w-3 h-3 text-orange-600" />
                  <span className="text-xs font-medium text-gray-700">Faith Guided</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {data.quant_rollups.categorical_distributions.faith_guided_pct}%
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Activity className="w-3 h-3 text-red-600" />
                  <span className="text-xs font-medium text-gray-700">Time Pressure</span>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {data.quant_rollups.categorical_distributions.time_pressure_pct}%
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to format percentage - only add % if not already present
  const formatPercentage = (value: string | number): string => {
    const strValue = String(value);
    if (strValue.includes('%')) {
      return strValue;
    }
    return `${strValue}%`;
  };

  // Small component for displaying key-value pairs
  const InfoItem: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
    <div className="flex items-center gap-2 text-xs text-gray-600">
      <div className="text-gray-400">{icon}</div>
      <span className="font-medium">{label}:</span>
      <span>{value}</span>
    </div>
  );

  // Small component for lists with expand/collapse
  const InfoList: React.FC<{ icon: React.ReactNode; label: string; items: string[]; limit?: number; sectionKey: string }> = ({ 
    icon, label, items, limit = 6, sectionKey 
  }) => {
    const isExpanded = expandedSections[sectionKey];
    const displayItems = isExpanded ? items : items.slice(0, limit);
    const hasMore = items.length > limit;

    const toggleExpanded = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection
      setExpandedSections(prev => ({
        ...prev,
        [sectionKey]: !prev[sectionKey]
      }));
    };

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
          <div className="text-gray-400">{icon}</div>
          <span>{label}:</span>
        </div>
        <div className="ml-6 space-y-0.5">
          {displayItems.map((item, index) => (
            <div key={index} className="text-xs text-gray-600">• {item}</div>
          ))}
          {hasMore && (
            <button
              onClick={toggleExpanded}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  +{items.length - limit} more
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Deep Segmentation Analysis renderer (for SmartyPants segments)
  const renderDeepSegmentationAnalysis = (data: any, segmentName: string) => {

    return (
      <div className="space-y-6 font-['Inter',sans-serif]">
        {/* Hero Banner with Segment Name & Tagline */}
        {data.segment_name_tagline && (
          <div className="bg-gradient-to-r from-[#E8FCFA] to-white border border-[#07E5D1]/40 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-[#1C1C1E] mb-2 tracking-tight">
              {segmentName}
            </h2>
            <p className="text-[#374151] italic text-lg leading-relaxed font-normal">
              "{data.segment_name_tagline.tagline}"
            </p>
          </div>
        )}

        {/* Size & Strategic Importance */}
        {data.size_importance && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#E8FCFA] border border-[#07E5D1]/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-[#07E5D1]" />
                <h4 className="text-base font-semibold text-[#1C1C1E]">Market Size</h4>
              </div>
              <p className="text-sm text-[#374151] leading-relaxed">{data.size_importance.size_estimate}</p>
            </div>
            <div className="bg-[#E8FCFA] border border-[#07E5D1]/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-[#07E5D1]" />
                <h4 className="text-base font-semibold text-[#1C1C1E]">Growth Trajectory</h4>
              </div>
              <p className="text-sm text-[#374151] leading-relaxed">{data.size_importance.growth_trajectory}</p>
            </div>
          </div>
        )}

        {/* Core Psychological Signature */}
        {data.psychological_signature && (
          <div className="border border-[#07E5D1]/30 rounded-lg overflow-hidden">
            <div className="bg-[#E8FCFA] px-4 py-3 border-b border-[#07E5D1]/30">
              <h3 className="text-lg font-semibold text-[#1C1C1E] flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#07E5D1]" />
                Core Psychological Signature
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {data.psychological_signature.functional_job && (
                <div>
                  <h5 className="text-sm font-semibold text-[#1C1C1E] mb-1">Functional Job</h5>
                  <p className="text-sm text-[#374151] italic leading-relaxed">"{data.psychological_signature.functional_job}"</p>
                </div>
              )}
              {data.psychological_signature.emotional_job && (
                <div>
                  <h5 className="text-sm font-semibold text-[#1C1C1E] mb-1">Emotional Job</h5>
                  <p className="text-sm text-[#374151] italic leading-relaxed">"{data.psychological_signature.emotional_job}"</p>
                </div>
              )}
              {data.psychological_signature.social_job && (
                <div>
                  <h5 className="text-sm font-semibold text-[#1C1C1E] mb-1">Social Job</h5>
                  <p className="text-sm text-[#374151] italic leading-relaxed">"{data.psychological_signature.social_job}"</p>
                </div>
              )}
              {data.psychological_signature.core_worldview && (
                <div className="mt-4 pt-4 border-t border-[#07E5D1]/20">
                  <h5 className="text-sm font-semibold text-[#1C1C1E] mb-2">Core Worldview</h5>
                  <div className="bg-[#E8FCFA] rounded-lg p-3">
                    <p className="text-sm text-[#374151] font-medium leading-relaxed">"{data.psychological_signature.core_worldview}"</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emotional Landscape */}
        {data.emotional_landscape?.baseline_state && (
          <div className="bg-[#E8FCFA] border border-[#07E5D1]/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-5 h-5 text-[#07E5D1]" />
              <h4 className="text-base font-semibold text-[#1C1C1E]">Emotional Baseline</h4>
            </div>
            <p className="text-sm text-[#374151] leading-relaxed">{data.emotional_landscape.baseline_state}</p>
          </div>
        )}

        {/* Day in the Life */}
        {data.day_in_the_life && (
          <div className="border border-[#07E5D1]/30 rounded-lg overflow-hidden">
            <div className="bg-[#E8FCFA] px-4 py-3 border-b border-[#07E5D1]/30">
              <h3 className="text-lg font-semibold text-[#1C1C1E] flex items-center gap-2">
                <User className="w-5 h-5 text-[#07E5D1]" />
                A Day in the Life
              </h3>
            </div>
            <div className="p-4">
              <p className="text-sm text-[#374151] leading-relaxed italic">{data.day_in_the_life}</p>
            </div>
          </div>
        )}

        {/* Screener Questions */}
        {data.screener_questions && data.screener_questions.length > 0 && (
          <div className="border border-[#07E5D1]/30 rounded-lg overflow-hidden">
            <div className="bg-[#E8FCFA] px-4 py-3 border-b border-[#07E5D1]/30">
              <h3 className="text-lg font-semibold text-[#1C1C1E] flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#07E5D1]" />
                How You'll Know It's Them
              </h3>
            </div>
            <div className="p-4">
              <ol className="space-y-3">
                {data.screener_questions.map((q: string, i: number) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#374151]">
                    <span className="bg-[#E8FCFA] text-[#07E5D1] border border-[#07E5D1]/30 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-semibold text-xs">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{q}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {/* Raw Markdown fallback - expandable */}
        {data.raw_markdown && (
          <details className="border border-gray-200 rounded-lg overflow-hidden">
            <summary className="bg-gray-50 px-4 py-3 cursor-pointer font-medium text-gray-700 hover:bg-gray-100">
              View Full Analysis (Markdown)
            </summary>
            <div className="p-4 prose prose-sm max-w-none">
              <ReactMarkdown>{data.raw_markdown}</ReactMarkdown>
            </div>
          </details>
        )}
      </div>
    );
  };

  const renderSegmentDetails = (forceExpanded: boolean = false) => {
    // Check for deep segmentation analysis format (SmartyPants segments)
    // Data can be at root level OR in demographics.segments[0]
    const deepSegmentData = demographics?.analysis_type === 'deep_segmentation' ? demographics :
                            demographics?.segments?.[0]?.analysis_type === 'deep_segmentation' ? demographics?.segments?.[0] : null;

    if (deepSegmentData) {
      // Pass the actual data and segment name to the renderer
      return renderDeepSegmentationAnalysis(deepSegmentData, segment.name);
    }

    // Check if data is in new format (segments 130-133)
    // New format can be either directly in demographics OR in demographics.segments[0]
    const newFormatData = demographics?.exec_summary ? demographics :
                          demographics?.segments?.[0]?.exec_summary ? demographics?.segments?.[0] : null;

    if (newFormatData?.exec_summary) {
      // Create a temporary demographics object with the new format data at root level
      const formattedDemographics = {
        ...newFormatData,
        exec_summary: newFormatData.exec_summary,
        macro_clusters: newFormatData.macro_clusters,
        quant_rollups: newFormatData.quant_rollups
      };

      // Temporarily override demographics for rendering
      const originalDemographics = demographics;
      // @ts-ignore
      demographics = formattedDemographics;
      const result = renderNewFormatDetails();
      // @ts-ignore
      demographics = originalDemographics;
      return result;
    }
    
    // Handle both old and new API structures for old format
    const overview = demographics?.segment_overviews?.[0] || demographics?.segments?.[0];
    if (!overview) return null;
    
    // Extract data from new API structure
    const totalPersonas = overview.total_personas || overview.persona_count || segment.len;

    // Helper function to render percentage tags with expand/collapse
    const renderPercentageTags = (items: any[], colorClass: string, sectionKey: string, limit: number = 6) => {
      if (!items || items.length === 0) return null;
      
      // Filter out items with undefined values - support all API structures
      const validItems = items.filter((item: any) => {
        if (!item) return false;
        
        if (typeof item === 'object') {
          const mainValue = item?.city || item?.level || item?.name || item?.industry || item?.occupation || item?.region || item?.category || item?.education_level || item?.education;
          if (mainValue && mainValue !== 'undefined' && mainValue.trim() !== '') return true;
          const keys = Object.keys(item);
          if (keys.length > 0) {
            const firstKey = keys[0];
            return firstKey && firstKey !== 'undefined' && firstKey.trim() !== '';
          }
          return false;
        }
        return item && item !== 'undefined' && String(item).trim() !== '';
      });
      
      if (validItems.length === 0) return null;
      
      const isExpanded = forceExpanded || expandedSections[sectionKey];
      const displayItems = isExpanded ? validItems : validItems.slice(0, limit);
      const hasMore = validItems.length > limit;
      
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {displayItems.map((item: any, index: number) => {
              let displayText = '';
              if (typeof item === 'object') {
                // Handle different location structures
                if (item.city) {
                  // Structure: {city, state/country, percentage}
                  displayText = `${item.city}${item.state || item.country ? `, ${item.state || item.country}` : ''} (${formatPercentage(item.percentage)})`;
                } else if (item.state && item.percentage) {
                  // Structure: {state, count, cities[], percentage}
                  displayText = `${item.state} (${formatPercentage(item.percentage)})`;
                } else if (item.region) {
                  // Structure: {region, cities[], percentage}
                  displayText = `${item.region} (${formatPercentage(item.percentage)})`;
                } else if (item.location) {
                  // Structure: {location, characteristics, percentage} - NEW API FORMAT
                  displayText = `${item.location} (${formatPercentage(item.percentage)})`;
                } else if (item.category) {
                  // Structure: {category, roles[], percentage} 
                  displayText = `${item.category} (${formatPercentage(item.percentage)})`;
                } else if (item.education_level) {
                  // Structure: {education_level, count, percentage} - NEW API FORMAT
                  displayText = `${item.education_level} (${formatPercentage(item.percentage)})`;
                } else if (item.education) {
                  // Structure: {education, percentage} - NEWER API FORMAT
                  displayText = `${item.education} (${formatPercentage(item.percentage)})`;
                } else if (item.industry) {
                  // Structure: {industry, count, percentage} - NEW API FORMAT
                  displayText = `${item.industry} (${formatPercentage(item.percentage)})`;
                } else if (item.occupation) {
                  // Structure: {occupation, count, percentage} - NEW API FORMAT
                  displayText = `${item.occupation} (${formatPercentage(item.percentage)})`;
                } else {
                  const mainValue = item?.level || item?.name;
                  if (mainValue && item.percentage !== undefined) {
                    displayText = `${mainValue} (${formatPercentage(item.percentage)})`;
                  } else {
                    const keys = Object.keys(item);
                    if (keys.length > 0) {
                      const key = keys[0];
                      const val = item[key];
                      displayText = typeof val === 'string'
                        ? `${key} (${val.includes('%') ? val : formatPercentage(val)})`
                        : key;
                    }
                  }
                }
              } else {
                displayText = String(item);
              }
              
              return (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
                >
                  {displayText}
                </span>
              );
            })}
          </div>
          {hasMore && !forceExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSections(prev => ({
                  ...prev,
                  [sectionKey]: !prev[sectionKey]
                }));
              }}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  +{validItems.length - limit} more
                </>
              )}
            </button>
          )}
        </div>
      );
    };

    // Helper function to render collapsible sections
    const renderExpandableSection = (
      title: string, 
      icon: React.ReactNode, 
      items: any[], 
      sectionKey: string,
      colorClass: string = 'text-gray-700',
      maxShow: number = 6
    ) => {
      if (!items || items.length === 0) return null;
      
      const isExpanded = forceExpanded || expandedSections[sectionKey];
      const displayItems = isExpanded ? items : items.slice(0, maxShow);
      const hasMore = items.length > maxShow;

      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className={`${colorClass}`}>{icon}</div>
            <span className="text-xs font-medium text-gray-700">{title}:</span>
          </div>
          <div className="ml-6 space-y-1">
            {displayItems.map((item: any, index: number) => {
              let displayText = '';
              if (typeof item === 'object' && item !== null) {
                // Handle behavioral patterns - support both old (pattern) and new (description) API structures
                if (item.pattern || item.description) {
                  const mainText = item.pattern || item.description;
                  displayText = `${mainText}${item.frequency ? ` (${item.frequency})` : ''}`;
                }
                // Handle communication preferences - NEW API structure
                else if (item.channel && item.preference) {
                  displayText = `${item.channel}: ${item.preference}`;
                }
                // Handle purchasing behaviors - NEW API structure
                else if (item.decision_factor) {
                  displayText = `${item.decision_factor} - ${item.description}${item.importance ? ` (${item.importance})` : ''}`;
                }
                // Handle communication preferences - support format + style structure
                else if (item.format || item.style || item.tone) {
                  const mainText = item.format || item.description || item.pattern || '';
                  const styleText = item.style || item.tone || '';
                  displayText = `${mainText}${styleText ? ` - ${styleText}` : ''}`;
                }
                // Handle purchasing behaviors - support behavior + timeline structure
                else if (item.criteria || item.behavior) {
                  const mainText = item.criteria || item.behavior;
                  const additionalInfo = item.decision_process || item.timeline || '';
                  displayText = `${mainText}${additionalInfo ? ` - ${additionalInfo}` : ''}`;
                }
                // Fallback for other objects
                else {
                  displayText = Object.values(item).filter(v => typeof v === 'string').join(' - ') || JSON.stringify(item);
                }
              } else {
                displayText = String(item);
              }

              return (
                <div key={index} className="text-xs text-gray-600">
                  • {displayText}
                </div>
              );
            })}
            {hasMore && !forceExpanded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedSections(prev => ({
                    ...prev,
                    [sectionKey]: !prev[sectionKey]
                  }));
                }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline cursor-pointer"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    +{items.length - maxShow} more
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      );
    };

    const isDetailsExpanded = showMoreDetails[segment.id] || false;

    return (
      <div className="mt-4 space-y-4">
        {/* Segment Summary - Show this first without the Overview card wrapper */}
        {overview.segment_summary && (
          <div className="text-sm text-gray-600 leading-relaxed">
            {overview.segment_summary}
          </div>
        )}
        
        {/* Overview Section - No blue card wrapper */}
        <div className="space-y-4">

          {/* Basic Stats Row */}
          <div className="grid grid-cols-2 gap-3">
            <InfoItem 
              icon={<Users className="w-3 h-3" />}
              label="Total"
              value={`${totalPersonas} personas (${totalAudienceSize ? ((totalPersonas / totalAudienceSize) * 100).toFixed(1) : '0.0'}%)`}
            />
            {(overview.age_range || overview.age_analysis) && (
              overview.age_analysis?.distribution ? (
                <div className="space-y-2">
                  <InfoItem 
                    icon={<Calendar className="w-3 h-3" />}
                    label="Age Range"
                    value={`${overview.age_analysis.min_value}-${overview.age_analysis.max_value} years` || overview.age_analysis.most_common || 'Mixed ages'}
                  />
                  <div className="ml-6 space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(overview.age_analysis.distribution).map(([range, count]: [string, any]) => {
                        const total = overview.total_personas || Object.values(overview.age_analysis.distribution).reduce((a: number, b: any) => a + Number(b), 0);
                        const percentage = ((count / total) * 100).toFixed(1);
                        return (
                          <span
                            key={range}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                          >
                            {range}: {count.toLocaleString()} ({formatPercentage(percentage)})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <InfoItem 
                  icon={<Calendar className="w-3 h-3" />}
                  label="Age"
                  value={overview.age_range || overview.age_analysis?.most_common}
                />
              )
            )}
          </div>

          {/* Income - Enhanced display with full distribution */}
          {(overview.income_range || overview.income_analysis) && (
            overview.income_analysis?.distribution ? (
              <div className="space-y-2">
                <InfoItem 
                  icon={<DollarSign className="w-3 h-3 text-green-600" />}
                  label="Income Range"
                  value={`$${overview.income_analysis.min_value?.toLocaleString()}-$${overview.income_analysis.max_value?.toLocaleString()}` || overview.income_analysis.most_common || 'Mixed incomes'}
                />
                <div className="ml-6 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(overview.income_analysis.distribution).map(([range, count]: [string, any]) => {
                      const total = overview.total_personas || Object.values(overview.income_analysis.distribution).reduce((a: number, b: any) => a + Number(b), 0);
                      const percentage = ((count / total) * 100).toFixed(1);
                      
                      // Format range with proper currency formatting
                      let displayRange = range;
                      if (!range.includes('$')) {
                        // If no $ sign, try to format as currency
                        const rangeMatch = range.match(/(\d+)(?:\s*-\s*(\d+))?/);
                        if (rangeMatch) {
                          const min = Number(rangeMatch[1]).toLocaleString();
                          const max = rangeMatch[2] ? Number(rangeMatch[2]).toLocaleString() : null;
                          displayRange = max ? `$${min}-$${max}` : `$${min}`;
                        } else {
                          displayRange = `$${range}`;
                        }
                      }
                      
                      return (
                        <span
                          key={range}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200"
                        >
                          {displayRange}: {count.toLocaleString()} ({formatPercentage(percentage)})
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <InfoItem 
                icon={<DollarSign className="w-3 h-3 text-green-600" />}
                label="Income"
                value={overview.income_range || overview.income_analysis?.most_common}
              />
            )
          )}

          {/* Locations - Direct display */}
          {(() => {
            // Filter out invalid location objects (e.g., those with only 'count' keys)
            const validLocations = overview.locations?.filter((item: any) => {
              if (!item || typeof item !== 'object') return false;
              // Must have at least one valid location identifier
              const hasValidLocation = !!(item.location || item.state || item.city || item.region);
              // Must have a valid percentage
              const hasValidPercentage = item.percentage !== undefined && item.percentage !== null;
              // Exclude objects that only have 'count' key(s) without location data
              const keys = Object.keys(item);
              const isOnlyCountKeys = keys.every(k => k === 'count' || k === 'counts');
              return hasValidLocation && hasValidPercentage && !isOnlyCountKeys;
            }) || [];

            return validLocations.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-purple-600" />
                  <span className="text-xs font-medium text-gray-700">Locations:</span>
                </div>
                <div className="ml-6 flex flex-wrap gap-1.5">
                  {validLocations.map((item: any, index: number) => {
                    let displayText = '';
                    if (item.location && item.percentage) {
                      displayText = `${item.location} (${formatPercentage(item.percentage)})`;
                    } else if (item.state && item.percentage) {
                      displayText = `${item.state} (${formatPercentage(item.percentage)})`;
                    } else if (item.city && item.percentage) {
                      displayText = `${item.city} (${formatPercentage(item.percentage)})`;
                    } else if (item.region && item.percentage) {
                      displayText = `${item.region} (${formatPercentage(item.percentage)})`;
                    }

                    return displayText && (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                      >
                        {displayText}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Education Levels */}
          {(() => {
            const validEducation = overview.education_levels?.filter((item: any) => {
              // Support all API structures: education, education_level, level, name
              const level = item.education || item.education_level || item.level || item.name || item;
              return level && level !== 'undefined' && String(level).trim() !== '';
            });
            return validEducation && validEducation.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-3 h-3 text-blue-600" />
                  <span className="text-xs font-medium text-gray-700">Education:</span>
                </div>
                <div className="ml-6">
                  {renderPercentageTags(overview.education_levels, 'bg-blue-100 text-blue-800 border border-blue-200', `education-${segment.id}`)}
                </div>
              </div>
            );
          })()}

          {/* Industries */}
          {(() => {
            const validIndustries = overview.industries?.filter((item: any) => {
              // First check if item exists
              if (!item) return false;
              // Support both old and new API structures
              const industry = item?.industry || item?.name || item;
              return industry && industry !== 'undefined' && String(industry).trim() !== '';
            });
            return validIndustries && validIndustries.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building className="w-3 h-3 text-indigo-600" />
                  <span className="text-xs font-medium text-gray-700">Industries:</span>
                </div>
                <div className="ml-6">
                  {renderPercentageTags(overview.industries, 'bg-indigo-100 text-indigo-800 border border-indigo-200', `industries-${segment.id}`)}
                </div>
              </div>
            );
          })()}

          {/* Occupations */}
          {(() => {
            const validOccupations = overview.occupations?.filter((item: any) => {
              // Support all API structures: occupation, name, category
              const occupation = item.occupation || item.name || item.category || item;
              return occupation && occupation !== 'undefined' && String(occupation).trim() !== '';
            });
            return validOccupations && validOccupations.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-3 h-3 text-amber-600" />
                  <span className="text-xs font-medium text-gray-700">Occupations:</span>
                </div>
                <div className="ml-6">
                  {renderPercentageTags(overview.occupations, 'bg-amber-100 text-amber-800 border border-amber-200', `occupations-${segment.id}`)}
                </div>
              </div>
            );
          })()}


            {/* Key Characteristics - keep outside dropdown */}
            {overview.key_characteristics && overview.key_characteristics.length > 0 && 
              renderExpandableSection(
                'Key Characteristics', 
                <Star className="w-3 h-3" />, 
                overview.key_characteristics, 
                `characteristics-${segment.id}`,
                'text-yellow-600'
              )}
        </div>

        {/* More Details - Show directly when forceExpanded, otherwise as dropdown */}
        {(overview.behavioral_patterns || overview.goals_and_aspirations || overview.pain_points || overview.brand_preferences || 
          overview.channel_preferences || overview.values_and_motivations || overview.communication_preferences || 
          overview.purchasing_behaviors || overview.marketing_opportunities || overview.messaging_recommendations || 
          overview.differentiation_factors) && (
          forceExpanded ? (
            <div className="space-y-4">
              {/* Behavioral Patterns */}
              {renderExpandableSection(
                'Behavioral Patterns', 
                <TrendingUp className="w-3 h-3" />, 
                overview.behavioral_patterns, 
                `behaviors-${segment.id}`,
                'text-green-600'
              )}

              {/* Goals & Aspirations */}
              {renderExpandableSection(
                'Goals & Aspirations', 
                <Target className="w-3 h-3" />, 
                overview.goals_and_aspirations, 
                `goals-${segment.id}`,
                'text-blue-600'
              )}

              {/* Pain Points */}
              {renderExpandableSection(
                'Pain Points', 
                <AlertTriangle className="w-3 h-3" />, 
                overview.pain_points, 
                `pain-points-${segment.id}`,
                'text-red-600'
              )}

              {/* Brand Preferences */}
              {renderExpandableSection(
                'Brand Preferences', 
                <Heart className="w-3 h-3" />, 
                overview.brand_preferences, 
                `brands-${segment.id}`,
                'text-pink-600'
              )}

              {/* Channel Preferences */}
              {renderExpandableSection(
                'Channel Preferences', 
                <MessageSquare className="w-3 h-3" />, 
                overview.channel_preferences, 
                `channels-${segment.id}`,
                'text-indigo-600'
              )}

              {/* Values & Motivations */}
              {renderExpandableSection(
                'Values & Motivations', 
                <Lightbulb className="w-3 h-3" />, 
                overview.values_and_motivations, 
                `values-${segment.id}`,
                'text-purple-600'
              )}

              {/* Communication Preferences */}
              {renderExpandableSection(
                'Communication Preferences', 
                <MessageSquare className="w-3 h-3" />, 
                overview.communication_preferences, 
                `communication-${segment.id}`,
                'text-cyan-600'
              )}

              {/* Purchasing Behaviors */}
              {renderExpandableSection(
                'Purchasing Behaviors', 
                <ShoppingCart className="w-3 h-3" />, 
                overview.purchasing_behaviors, 
                `purchasing-${segment.id}`,
                'text-orange-600'
              )}

              {/* Marketing Opportunities */}
              {renderExpandableSection(
                'Marketing Opportunities', 
                <Megaphone className="w-3 h-3" />, 
                overview.marketing_opportunities, 
                `marketing-${segment.id}`,
                'text-emerald-600'
              )}

              {/* Messaging Recommendations */}
              {renderExpandableSection(
                'Messaging Recommendations', 
                <Zap className="w-3 h-3" />, 
                overview.messaging_recommendations, 
                `messaging-${segment.id}`,
                'text-violet-600'
              )}

              {/* Differentiation Factors */}
              {renderExpandableSection(
                'Differentiation Factors', 
                <Star className="w-3 h-3" />, 
                overview.differentiation_factors, 
                `differentiation-${segment.id}`,
                'text-rose-600'
              )}
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMoreDetails(prev => ({ ...prev, [segment.id]: !prev[segment.id] }));
                }}
                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-sm font-medium text-gray-700"
              >
                <span>More Details</span>
                {isDetailsExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>
              
              {isDetailsExpanded && (
                <div className="p-4 space-y-4 bg-white animate-fadeIn">
                  {/* Behavioral Patterns */}
                  {renderExpandableSection(
                    'Behavioral Patterns', 
                    <TrendingUp className="w-3 h-3" />, 
                    overview.behavioral_patterns, 
                    `behaviors-${segment.id}`,
                    'text-green-600'
                  )}

                  {/* Goals & Aspirations */}
                  {renderExpandableSection(
                    'Goals & Aspirations', 
                    <Target className="w-3 h-3" />, 
                    overview.goals_and_aspirations, 
                    `goals-${segment.id}`,
                    'text-blue-600'
                  )}

                  {/* Pain Points */}
                  {renderExpandableSection(
                    'Pain Points', 
                    <AlertTriangle className="w-3 h-3" />, 
                    overview.pain_points, 
                    `pain-points-${segment.id}`,
                    'text-red-600'
                  )}

                  {/* Brand Preferences */}
                  {renderExpandableSection(
                    'Brand Preferences', 
                    <Heart className="w-3 h-3" />, 
                    overview.brand_preferences, 
                    `brands-${segment.id}`,
                    'text-pink-600'
                  )}

                  {/* Channel Preferences */}
                  {renderExpandableSection(
                    'Channel Preferences', 
                    <MessageSquare className="w-3 h-3" />, 
                    overview.channel_preferences, 
                    `channels-${segment.id}`,
                    'text-indigo-600'
                  )}

                  {/* Values & Motivations */}
                  {renderExpandableSection(
                    'Values & Motivations', 
                    <Lightbulb className="w-3 h-3" />, 
                    overview.values_and_motivations, 
                    `values-${segment.id}`,
                    'text-purple-600'
                  )}

                  {/* Communication Preferences */}
                  {renderExpandableSection(
                    'Communication Preferences', 
                    <MessageSquare className="w-3 h-3" />, 
                    overview.communication_preferences, 
                    `communication-${segment.id}`,
                    'text-cyan-600'
                  )}

                  {/* Purchasing Behaviors */}
                  {renderExpandableSection(
                    'Purchasing Behaviors', 
                    <ShoppingCart className="w-3 h-3" />, 
                    overview.purchasing_behaviors, 
                    `purchasing-${segment.id}`,
                    'text-orange-600'
                  )}

                  {/* Marketing Opportunities */}
                  {renderExpandableSection(
                    'Marketing Opportunities', 
                    <Megaphone className="w-3 h-3" />, 
                    overview.marketing_opportunities, 
                    `marketing-${segment.id}`,
                    'text-emerald-600'
                  )}

                  {/* Messaging Recommendations */}
                  {renderExpandableSection(
                    'Messaging Recommendations', 
                    <Zap className="w-3 h-3" />, 
                    overview.messaging_recommendations, 
                    `messaging-${segment.id}`,
                    'text-violet-600'
                  )}

                  {/* Differentiation Factors */}
                  {renderExpandableSection(
                    'Differentiation Factors', 
                    <Star className="w-3 h-3" />, 
                    overview.differentiation_factors, 
                    `differentiation-${segment.id}`,
                    'text-rose-600'
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };


  const renderInlineFilters = () => {
    if (Object.keys(filterConfig).length === 0) return null;

    return (
      <div 
        className={cn(
          "space-y-5 mt-4 animate-fadeIn flex-grow transition-opacity duration-200",
          !isSelected && "opacity-50 pointer-events-none"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-t border-gray-200 pt-3 mb-3 flex justify-between items-center">
          <p className="text-xs text-gray-500 italic">Segment filters:</p>
          <div className="flex gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllFilters();
              }}
              disabled={!isSelected}
              className={cn(
                "text-xs transition-colors",
                isSelected 
                  ? "text-gray-500 hover:text-gray-700 hover:underline cursor-pointer" 
                  : "text-gray-400 cursor-not-allowed"
              )}
            >
              Clear
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                selectAllFilters();
              }}
              disabled={!isSelected}
              className={cn(
                "text-xs transition-colors",
                isSelected 
                  ? "text-blue-600 hover:text-blue-800 hover:underline cursor-pointer" 
                  : "text-blue-400 cursor-not-allowed"
              )}
            >
              Select All
            </button>
          </div>
        </div>

        {/* Render filters dynamically based on filterConfig */}
        {Object.entries(filterConfig).map(([filterKey, config]: [string, any]) => {
          const options = config.options || [];
          if (options.length === 0) return null;

          // Get appropriate icon and color based on filter type
          let icon, headerColorClass, selectedColorClasses;
          switch (filterKey) {
            case 'age_group':
              icon = <User className="w-4 h-4" />;
              headerColorClass = 'text-blue-600';
              selectedColorClasses = 'peer-checked:bg-blue-100 peer-checked:text-blue-800 peer-checked:border-blue-300';
              break;
            case 'household_income':
              icon = <DollarSign className="w-4 h-4" />;
              headerColorClass = 'text-green-600';
              selectedColorClasses = 'peer-checked:bg-green-100 peer-checked:text-green-800 peer-checked:border-green-300';
              break;
            case 'geo_location':
              icon = <MapPin className="w-4 h-4" />;
              headerColorClass = 'text-purple-600';
              selectedColorClasses = 'peer-checked:bg-purple-100 peer-checked:text-purple-800 peer-checked:border-purple-300';
              break;
            case 'pets':
              icon = <User className="w-4 h-4" />;
              headerColorClass = 'text-orange-600';
              selectedColorClasses = 'peer-checked:bg-orange-100 peer-checked:text-orange-800 peer-checked:border-orange-300';
              break;
            case 'children':
              icon = <Users className="w-4 h-4" />;
              headerColorClass = 'text-pink-600';
              selectedColorClasses = 'peer-checked:bg-pink-100 peer-checked:text-pink-800 peer-checked:border-pink-300';
              break;
            case 'marital_status':
              icon = <User className="w-4 h-4" />;
              headerColorClass = 'text-indigo-600';
              selectedColorClasses = 'peer-checked:bg-indigo-100 peer-checked:text-indigo-800 peer-checked:border-indigo-300';
              break;
            case 'industryL1':
              icon = <Building2 className="w-4 h-4" />;
              headerColorClass = 'text-blue-600';
              selectedColorClasses = 'peer-checked:bg-blue-100 peer-checked:text-blue-800 peer-checked:border-blue-300';
              break;
            case 'industryL2':
              icon = <Building className="w-4 h-4" />;
              headerColorClass = 'text-indigo-600';
              selectedColorClasses = 'peer-checked:bg-indigo-100 peer-checked:text-indigo-800 peer-checked:border-indigo-300';
              break;
            case 'functions':
              icon = <Briefcase className="w-4 h-4" />;
              headerColorClass = 'text-green-600';
              selectedColorClasses = 'peer-checked:bg-green-100 peer-checked:text-green-800 peer-checked:border-green-300';
              break;
            case 'roles':
              icon = <Users className="w-4 h-4" />;
              headerColorClass = 'text-amber-600';
              selectedColorClasses = 'peer-checked:bg-amber-100 peer-checked:text-amber-800 peer-checked:border-amber-300';
              break;
            case 'subSegments':
              icon = <Target className="w-4 h-4" />;
              headerColorClass = 'text-teal-600';
              selectedColorClasses = 'peer-checked:bg-teal-100 peer-checked:text-teal-800 peer-checked:border-teal-300';
              break;
            // Health conscious persona filters
            case 'age_range':
              icon = <User className="w-4 h-4" />;
              headerColorClass = 'text-blue-600';
              selectedColorClasses = 'peer-checked:bg-blue-100 peer-checked:text-blue-800 peer-checked:border-blue-300';
              break;
            case 'income_bracket':
              icon = <DollarSign className="w-4 h-4" />;
              headerColorClass = 'text-green-600';
              selectedColorClasses = 'peer-checked:bg-green-100 peer-checked:text-green-800 peer-checked:border-green-300';
              break;
            case 'education_level':
              icon = <GraduationCap className="w-4 h-4" />;
              headerColorClass = 'text-purple-600';
              selectedColorClasses = 'peer-checked:bg-purple-100 peer-checked:text-purple-800 peer-checked:border-purple-300';
              break;
            case 'location_type':
              icon = <MapPin className="w-4 h-4" />;
              headerColorClass = 'text-orange-600';
              selectedColorClasses = 'peer-checked:bg-orange-100 peer-checked:text-orange-800 peer-checked:border-orange-300';
              break;
            case 'subsegment':
              icon = <Target className="w-4 h-4" />;
              headerColorClass = 'text-teal-600';
              selectedColorClasses = 'peer-checked:bg-teal-100 peer-checked:text-teal-800 peer-checked:border-teal-300';
              break;
            default:
              icon = <BarChart3 className="w-4 h-4" />;
              headerColorClass = 'text-gray-600';
              selectedColorClasses = 'peer-checked:bg-gray-100 peer-checked:text-gray-800 peer-checked:border-gray-300';
          }

          // Check if this is a hierarchical filter with nested options
          if (isHierarchicalFilter(config)) {
            // Special handling for subSegments - only show options that match this segment's name
            if (filterKey === 'subSegments') {
              const segmentSubOptions = config.options[segment.name];
              if (!segmentSubOptions || !Array.isArray(segmentSubOptions)) {
                return null; // Don't show subSegments filter if no options for this segment
              }
              
              return (
                <div key={filterKey}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                    <span className={`mr-2 ${headerColorClass}`}>{icon}</span>
                    {config.label || filterKey.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {segmentSubOptions.map((option: string, i: number) => (
                      <div key={i} className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          id={`${filterKey}-${segment.id}-${i}`}
                          checked={isFilterSelected(filterKey, option)}
                          disabled={!isSelected}
                          className={cn(
                            "peer absolute opacity-0 w-0 h-0",
                            isSelected ? "cursor-pointer" : "cursor-not-allowed"
                          )}
                          onChange={(e) => toggleFilter(filterKey, option, e.target.checked)}
                        />
                        <label
                          htmlFor={`${filterKey}-${segment.id}-${i}`}
                          className={cn(
                            "inline-block px-3 py-1 select-none text-xs font-medium rounded-full border transition-colors",
                            isSelected 
                              ? `cursor-pointer ${selectedColorClasses} peer-not-checked:bg-white peer-not-checked:text-gray-700 peer-not-checked:border-gray-300 hover:border-gray-400`
                              : "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                          )}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            
            // For other hierarchical filters, show the full expandable structure
            return (
              <div key={filterKey}>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <span className={`mr-2 ${headerColorClass}`}>{icon}</span>
                  {config.label || filterKey.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                </h4>
                <div className="space-y-3">
                  {Object.entries(config.options).map(([parentKey, childOptions]: [string, any]) => {
                    const expansionKey = `${filterKey}-${parentKey}`;
                    const isExpanded = expandedHierarchicalFilters[expansionKey];
                    const childOptionsArray = Array.isArray(childOptions) ? childOptions : [];
                    
                    return (
                      <div key={parentKey} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Parent Category Header */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isSelected) toggleHierarchicalExpansion(filterKey, parentKey);
                          }}
                          disabled={!isSelected}
                          className={cn(
                            "w-full flex items-center justify-between p-3 transition-colors",
                            isSelected 
                              ? "bg-gray-50 hover:bg-gray-100 cursor-pointer"
                              : "bg-gray-100 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500 mr-2" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500 mr-2" />
                            )}
                            <span className="text-sm font-medium text-gray-800">{parentKey}</span>
                            <span className="ml-2 text-xs text-gray-500">({childOptionsArray.length} options)</span>
                          </div>
                        </button>
                        
                        {/* Child Options - Collapsible */}
                        {isExpanded && (
                          <div className="p-3 bg-white border-t border-gray-200">
                            <div className="flex flex-wrap gap-2">
                              {childOptionsArray.map((option: string, i: number) => (
                                <div key={i} className="relative inline-flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`${filterKey}-${segment.id}-${parentKey}-${i}`}
                                    checked={isFilterSelected(filterKey, option)}
                                    disabled={!isSelected}
                                    className={cn(
                                      "peer absolute opacity-0 w-0 h-0",
                                      isSelected ? "cursor-pointer" : "cursor-not-allowed"
                                    )}
                                    onChange={(e) => toggleFilter(filterKey, option, e.target.checked)}
                                  />
                                  <label
                                    htmlFor={`${filterKey}-${segment.id}-${parentKey}-${i}`}
                                    className={cn(
                                      "inline-block px-3 py-1 select-none text-xs font-medium rounded-full border transition-colors",
                                      isSelected 
                                        ? `cursor-pointer ${selectedColorClasses} peer-not-checked:bg-white peer-not-checked:text-gray-700 peer-not-checked:border-gray-300 hover:border-gray-400`
                                        : "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                                    )}
                                  >
                                    {option}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          // Regular flat options filter
          return (
            <div key={filterKey}>
              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                <span className={`mr-2 ${headerColorClass}`}>{icon}</span>
                {config.label || filterKey.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
              </h4>
              <div className="flex flex-wrap gap-2">
                {options.map((option: string, i: number) => (
                  <div key={i} className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      id={`${filterKey}-${segment.id}-${i}`}
                      checked={isFilterSelected(filterKey, option)}
                      disabled={!isSelected}
                      className={cn(
                        "peer absolute opacity-0 w-0 h-0",
                        isSelected ? "cursor-pointer" : "cursor-not-allowed"
                      )}
                      onChange={(e) => toggleFilter(filterKey, option, e.target.checked)}
                    />
                    <label
                      htmlFor={`${filterKey}-${segment.id}-${i}`}
                      className={cn(
                        "inline-block px-3 py-1 select-none text-xs font-medium rounded-full border transition-colors",
                        isSelected 
                          ? `cursor-pointer ${selectedColorClasses} peer-not-checked:bg-white peer-not-checked:text-gray-700 peer-not-checked:border-gray-300 hover:border-gray-400`
                          : "cursor-not-allowed bg-gray-100 text-gray-400 border-gray-200"
                      )}
                    >
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  return (
    <div className="relative">
      {/* Checkbox indicator in top-right corner */}
      <div className="absolute top-3 right-3 z-10">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(segment.id)}
            className="sr-only"
          />
          <div className={cn(
            "w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200",
            isSelected
              ? "bg-[#07E5D1] border-[#07E5D1]"
              : "bg-white border-gray-300 hover:border-gray-400"
          )}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </label>
      </div>
      
      <div
        className={cn(
          "border rounded-lg p-4 transition-all duration-200 cursor-pointer",
          isSelected
            ? 'bg-blue-50 border-blue-300 shadow-md'
            : 'border-gray-200 hover:border-blue-400 hover:shadow-sm',
          className
        )}
        onClick={handleCardClick}
      >
        <div className="w-full">
            {/* Header with Title */}
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-medium text-gray-900">{segment.name}</h3>
            </div>
            
            {/* Profile Count and View All Button */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">
                {loadingCount ? (
                  <span className="font-medium text-blue-600 flex items-center">
                    <div className="flex space-x-1 items-center">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </span>
                ) : onViewPersonas ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewPersonas(segment.id);
                    }}
                    title="Click to see all profiles"
                    className={`text-sm hover:underline transition-colors ${
                      personaCount !== undefined && personaCount !== segment.len 
                        ? "font-medium text-blue-600 hover:text-blue-700" 
                        : "text-blue-600 hover:text-blue-700"
                    }`}
                  >
                    {personaCount !== undefined ? personaCount : segment.len} profiles ({totalAudienceSize ? ((personaCount !== undefined ? personaCount : segment.len) / totalAudienceSize * 100).toFixed(1) : '0.0'}%)
                  </button>
                ) : (
                  <span className={personaCount !== undefined && personaCount !== segment.len ? "font-medium text-blue-600" : ""}>
                    {personaCount !== undefined ? personaCount : segment.len} profiles ({totalAudienceSize ? ((personaCount !== undefined ? personaCount : segment.len) / totalAudienceSize * 100).toFixed(1) : '0.0'}%)
                  </span>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDetailsModal(true);
                }}
                title="Click to see segment details"
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
              >
                <Info className="w-4 h-4 mr-1" />
                Segment Details
              </button>
            </div>

            {/* Segment Description (shown on hover of info button) */}
            {segment.description && (
              <div className="text-sm text-gray-600 leading-relaxed mb-3">
                <ReactMarkdown>{segment.description}</ReactMarkdown>
              </div>
            )}

          {/* Filter fields - Always shown */}
          {renderInlineFilters()}
        </div>
      </div>

      {/* Analytics Modal */}
      <SegmentAnalyticsModal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
        segmentName={segment.name}
        analyticsData={demographics as any}
      />

      {/* Details Modal */}
      {showDetailsModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${getZIndexClass('dialog')} p-4`}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{segment.name} - Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {renderSegmentDetails(true)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(SegmentCard);